import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lightbulb, Loader2, Sprout } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const hostSuggestionSchema = z.object({
  name: z.string().trim().min(1, 'Namn krävs').max(200),
  description: z.string().trim().min(1, 'Beskriv gärna vad de gör').max(1000),
  contact: z.string().trim().max(300).optional().or(z.literal('')),
});

type HostSuggestionForm = z.infer<typeof hostSuggestionSchema>;

interface HostSuggestionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The place this tip is about (optional context) */
  sourcePlaceId?: string;
  sourcePlaceName?: string;
}

export function HostSuggestionModal({
  open,
  onOpenChange,
  sourcePlaceId,
  sourcePlaceName,
}: HostSuggestionModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HostSuggestionForm>({
    resolver: zodResolver(hostSuggestionSchema),
    defaultValues: { name: '', description: '', contact: '' },
  });

  async function onSubmit(values: HostSuggestionForm) {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('place_suggestions')
        .insert({
          suggestion_type: 'host' as string,
          suggested_name: values.name,
          note: values.description,
          contact_info: values.contact || null,
          source_place_id: sourcePlaceId || null,
          producer_id: null as any,
          created_by: user?.id ?? null,
        });

      if (error) throw error;

      setSubmitted(true);
    } catch (err: any) {
      console.error('Host suggestion error:', err);
      toast({
        title: 'Något gick fel',
        description: 'Försök igen om en stund.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    onOpenChange(false);
    // Reset after animation
    setTimeout(() => {
      setSubmitted(false);
      reset();
    }, 200);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <div className="flex flex-col items-center text-center py-6 space-y-3">
            <Sprout className="h-8 w-8 text-primary" />
            <DialogTitle className="text-lg">Tack! 🌱</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              Vi kollar upp tipset och hör av oss.
            </DialogDescription>
            <Button variant="outline" size="sm" onClick={handleClose} className="mt-2">
              Stäng
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                <DialogTitle className="text-base">Tipsa om en värd</DialogTitle>
              </div>
              <DialogDescription className="text-sm leading-relaxed">
                {sourcePlaceName
                  ? `Känner du till en verksamhet som borde finnas vid ${sourcePlaceName}?`
                  : 'Känner du till en producent eller verksamhet som borde finnas med?'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="host-name">Namn på person eller verksamhet *</Label>
                <Input
                  id="host-name"
                  placeholder="T.ex. Yoga med Anna"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="host-desc">Vad gör de? *</Label>
                <Textarea
                  id="host-desc"
                  placeholder="Berätta kort om verksamheten…"
                  rows={3}
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-xs text-destructive">{errors.description.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="host-contact">Kontakt (valfritt)</Label>
                <Input
                  id="host-contact"
                  placeholder="E-post, telefon eller Instagram"
                  {...register('contact')}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Skickar…
                  </>
                ) : (
                  'Skicka tips'
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
